// /admin/porteros — Lista de porteros activos del programa de referidos.
// Para postulaciones nuevas ver /admin/postulaciones-porteros.

import { useEffect, useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import ExportButton from '../../components/admin/ExportButton'
import { fmt } from '../../lib/exportToCsv'

const COLUMNAS_PORTEROS = [
  { key: 'profile.nombre',  label: 'Nombre' },
  { key: 'profile.phone',   label: 'Teléfono' },
  { key: 'profile.email',   label: 'Email' },
  { key: 'edificio_nombre', label: 'Edificio' },
  { key: 'direccion',       label: 'Dirección' },
  { key: 'barrio.nombre',   label: 'Barrio' },
  { key: 'cant_unidades',   label: 'Unidades',  format: fmt.int },
  { key: 'turno',           label: 'Turno' },
  { key: 'activo',          label: 'Activo',    format: fmt.bool },
  { key: 'tyc_aceptado_at', label: 'T&C aceptado', format: fmt.date },
  { key: 'refs.total',      label: 'Clientes referidos', format: fmt.int },
  { key: 'refs.activos',    label: 'Clientes suscriptos activos', format: fmt.int },
  { key: 'banco_cuenta',    label: 'Cuenta banco' },
  { key: 'notas_pago',      label: 'Notas pago' },
  { key: 'slug',            label: 'Slug link público' },
  { key: 'created_at',      label: 'Alta',      format: fmt.date },
]

const NAVY = '#1A3461'
const TEAL = '#00A8B5'

export default function AdminPorteros() {
  const [porteros, setPorteros] = useState([])
  const [loading, setLoading] = useState(true)
  const [filtro, setFiltro] = useState('activos') // activos | todos
  const [busqueda, setBusqueda] = useState('')

  async function cargar() {
    setLoading(true)
    const { data, error } = await supabase
      .from('porteros')
      .select(`
        id, slug, lugar, edificio_nombre, direccion, turno, cant_unidades,
        activo, banco_cuenta, notas_pago, created_at, tyc_aceptado_at,
        profile:profiles!porteros_id_fkey(id, nombre, phone, email),
        barrio:barrios!porteros_barrio_id_fkey(id, nombre)
      `)
      .eq('tipo', 'portero')  // 2026-09-09: excluir vendedores (viven en /admin/vendedores)
      .order('created_at', { ascending: false })
    if (error) { console.error(error); setLoading(false); return }

    // Contar vecinos referidos por cada portero
    const ids = (data || []).map(p => p.id)
    let refCounts = {}
    if (ids.length > 0) {
      const { data: refs } = await supabase
        .from('clientes')
        .select('portero_referido_id, estado')
        .in('portero_referido_id', ids)
      for (const r of (refs || [])) {
        if (!refCounts[r.portero_referido_id]) refCounts[r.portero_referido_id] = { total: 0, activos: 0 }
        refCounts[r.portero_referido_id].total++
        if (r.estado === 'activa') refCounts[r.portero_referido_id].activos++
      }
    }

    setPorteros((data || []).map(p => ({ ...p, refs: refCounts[p.id] || { total: 0, activos: 0 } })))
    setLoading(false)
  }
  useEffect(() => { cargar() }, [])

  const filtrados = useMemo(() => {
    let arr = porteros
    if (filtro === 'activos') arr = arr.filter(p => p.activo)
    if (filtro === 'inactivos') arr = arr.filter(p => !p.activo)
    if (busqueda.trim()) {
      const q = busqueda.toLowerCase().trim()
      arr = arr.filter(p =>
        (p.profile?.nombre || '').toLowerCase().includes(q) ||
        (p.edificio_nombre || '').toLowerCase().includes(q) ||
        (p.direccion || '').toLowerCase().includes(q) ||
        (p.barrio?.nombre || '').toLowerCase().includes(q)
      )
    }
    return arr
  }, [porteros, filtro, busqueda])

  const stats = useMemo(() => {
    const activos = porteros.filter(p => p.activo)
    return {
      total: porteros.length,
      activos: activos.length,
      vecinosTotal: porteros.reduce((s, p) => s + (p.refs?.total || 0), 0),
      vecinosActivos: porteros.reduce((s, p) => s + (p.refs?.activos || 0), 0),
    }
  }, [porteros])

  return (
    <AdminLayout title="Porteros">
      <div style={{ maxWidth: 1200, margin: '0 auto', padding: '0 8px' }}>
        <div style={{ display:'flex', alignItems:'flex-end', justifyContent:'space-between', flexWrap:'wrap', gap: 10, margin:'8px 0 16px' }}>
          <div>
            <div style={{ fontSize: 22, fontWeight: 700, color: NAVY, letterSpacing: -0.4 }}>Porteros del programa</div>
            <div style={{ fontSize: 13, color: '#475569', marginTop: 3 }}>
              {stats.activos} activos · {stats.vecinosTotal} clientes referidos ({stats.vecinosActivos} suscriptos)
              {' · '}
              <Link to="/admin/postulaciones-porteros" style={{ color: TEAL, textDecoration: 'none' }}>Ver postulaciones nuevas →</Link>
            </div>
          </div>
          <div style={{ display:'flex', gap: 8 }}>
            <input
              type="search" placeholder="Buscar por nombre, edificio, barrio…"
              value={busqueda} onChange={e => setBusqueda(e.target.value)}
              style={{ padding:'8px 12px', border:'0.5px solid #E2E8F0', borderRadius: 6, fontSize: 13, width: 240, fontFamily:'inherit' }}
            />
            <select value={filtro} onChange={e => setFiltro(e.target.value)}
              style={{ padding:'8px 12px', border:'0.5px solid #E2E8F0', borderRadius: 6, fontSize: 13, fontFamily:'inherit' }}>
              <option value="activos">Activos ({stats.activos})</option>
              <option value="inactivos">Inactivos ({stats.total - stats.activos})</option>
              <option value="todos">Todos ({stats.total})</option>
            </select>
            <ExportButton data={filtrados} columns={COLUMNAS_PORTEROS} filename="porteros" label="Bajar a Sheets" />
          </div>
        </div>

        {loading ? (
          <div style={{ padding: 40, textAlign:'center', color:'#64748B' }}>Cargando…</div>
        ) : filtrados.length === 0 ? (
          <div style={{ padding: 40, textAlign:'center', color:'#64748B', background:'#fff', borderRadius: 8, border:'0.5px solid #E2E8F0' }}>
            {busqueda ? 'Sin resultados.' : 'No hay porteros para este filtro.'}
          </div>
        ) : (
          <div style={{ display:'grid', gap: 10 }}>
            {filtrados.map(p => <PorteroCard key={p.id} p={p} />)}
          </div>
        )}
      </div>
    </AdminLayout>
  )
}

function PorteroCard({ p }) {
  const tel = p.profile?.phone
  return (
    <div style={{ background:'#fff', border:'0.5px solid #E2E8F0', borderRadius: 10, padding: 14 }}>
      <div style={{ display:'flex', justifyContent:'space-between', gap: 12, marginBottom: 8, alignItems:'flex-start' }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ display:'flex', alignItems:'center', gap: 8, flexWrap:'wrap' }}>
            <span style={{ fontSize: 16, fontWeight: 700, color: NAVY }}>
              {p.profile?.nombre || '(sin nombre)'}
            </span>
            <span style={{
              fontSize: 10.5, padding:'2px 7px', borderRadius: 3, fontWeight: 700,
              background: p.activo ? 'rgba(22,163,74,.1)' : 'rgba(100,116,139,.15)',
              color: p.activo ? '#15803D' : '#475569',
            }}>
              {p.activo ? 'ACTIVO' : 'INACTIVO'}
            </span>
            {p.tyc_aceptado_at && (
              <span style={{ fontSize: 10.5, padding:'2px 7px', borderRadius: 3, fontWeight: 700, background:'rgba(0,168,181,.1)', color:'#0E7A82' }}>
                T&C ✓
              </span>
            )}
          </div>
          <div style={{ fontSize: 13, color:'#334155', marginTop: 4 }}>
            <strong>{p.edificio_nombre || p.lugar || '(sin edificio)'}</strong> · {p.direccion || '—'}{p.barrio?.nombre ? ` · ${p.barrio.nombre}` : ''}
          </div>
          <div style={{ fontSize: 12, color:'#64748B', marginTop: 3 }}>
            {p.cant_unidades ? `${p.cant_unidades} unidades` : '— unidades'}
            {p.turno && ` · ${p.turno}`}
            {tel && ` · ☎ ${tel}`}
            {p.profile?.email && ` · ${p.profile.email}`}
          </div>
        </div>
        <div style={{ textAlign:'right', flexShrink: 0 }}>
          <div style={{ fontSize: 24, fontWeight: 800, color: NAVY, lineHeight: 1 }}>
            {p.refs.total}
          </div>
          <div style={{ fontSize: 10.5, color:'#64748B', fontWeight: 600, letterSpacing: 0.3 }}>CLIENTES REFERIDOS</div>
          {p.refs.activos > 0 && (
            <div style={{ fontSize: 11.5, color:'#15803D', fontWeight: 700, marginTop: 2 }}>
              {p.refs.activos} suscriptos
            </div>
          )}
        </div>
      </div>

      {(p.banco_cuenta || p.notas_pago) && (
        <div style={{ marginTop: 8, padding:'8px 10px', background:'#F8FAFC', borderRadius: 6, fontSize: 12, color:'#475569' }}>
          {p.banco_cuenta && <div><strong>Cuenta:</strong> {p.banco_cuenta}</div>}
          {p.notas_pago && <div style={{ marginTop: 2 }}><strong>Notas:</strong> {p.notas_pago}</div>}
        </div>
      )}

      <div style={{ display:'flex', gap: 6, marginTop: 8 }}>
        <Link to={`/admin/porteros/${p.id}`} style={{ ...btnLink, background: NAVY, color:'#fff', borderColor: NAVY }}>
          Abrir ficha →
        </Link>
        {tel && (
          <a href={`https://wa.me/${String(tel).replace(/\D/g,'')}`} target="_blank" rel="noreferrer" style={{ ...btnLink, color:'#25D366', borderColor:'rgba(37,211,102,.4)' }}>
            WhatsApp
          </a>
        )}
      </div>
    </div>
  )
}

const btnLink = {
  fontSize: 12, padding:'5px 10px',
  border:'0.5px solid #CBD5E1', borderRadius: 6,
  color: NAVY, textDecoration:'none', fontWeight: 600,
  background:'#fff', cursor:'pointer',
}
